import type { GifEditAppearance } from '@buffer-mono/gif-toolkit'
import {
  GIF_EDIT_MAX_ADJUSTMENT,
  GIF_EDIT_MAX_FILTER_INTENSITY,
  GIF_EDIT_MIN_ADJUSTMENT,
  GIF_EDIT_MIN_FILTER_INTENSITY,
  isIdentityGifEditAppearance,
} from '@buffer-mono/gif-toolkit'

import { GIF_EDIT_MAX_STRAIGHTENING } from './limits'

export type GifEditRotation = 0 | 90 | 180 | 270

export type GifEditCrop = Readonly<{
  x: number
  y: number
  width: number
  height: number
}>

export type GifEditRecipe = GifEditAppearance &
  Readonly<{
    crop: GifEditCrop
    rotation: GifEditRotation
    straightening: number
    flipHorizontal: boolean
    flipVertical: boolean
    paletteMode: 'perFrame' | 'global'
  }>

export const IDENTITY_GIF_EDIT_CROP: GifEditCrop = {
  x: 0,
  y: 0,
  width: 1,
  height: 1,
}

const CROP_COMPARISON_TOLERANCE = 0.000_001

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

function assertFiniteNumber(
  value: unknown,
  name: string,
): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new TypeError(`${name} must be a finite number`)
  }
}

function assertIntegerInRange(
  value: unknown,
  minimum: number,
  maximum: number,
  name: string,
): asserts value is number {
  assertFiniteNumber(value, name)
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    throw new RangeError(
      `${name} must be an integer between ${minimum} and ${maximum}`,
    )
  }
}

function assertGifEditCrop(value: unknown): asserts value is GifEditCrop {
  if (!isRecord(value)) throw new TypeError('crop must be an object')

  const { x, y, width, height } = value
  assertFiniteNumber(x, 'crop.x')
  assertFiniteNumber(y, 'crop.y')
  assertFiniteNumber(width, 'crop.width')
  assertFiniteNumber(height, 'crop.height')

  if (
    width <= 0 ||
    height <= 0 ||
    x < -CROP_COMPARISON_TOLERANCE ||
    y < -CROP_COMPARISON_TOLERANCE ||
    x + width > 1 + CROP_COMPARISON_TOLERANCE ||
    y + height > 1 + CROP_COMPARISON_TOLERANCE
  ) {
    throw new RangeError('crop must remain within the normalized image bounds')
  }
}

export function assertGifEditRecipe(
  value: unknown,
): asserts value is GifEditRecipe {
  if (!isRecord(value)) throw new TypeError('recipe must be an object')

  const {
    crop,
    rotation,
    straightening,
    flipHorizontal,
    flipVertical,
    brightness,
    contrast,
    saturation,
    warmth,
    filter,
    filterIntensity,
    paletteMode,
  } = value

  assertGifEditCrop(crop)
  if (
    rotation !== 0 &&
    rotation !== 90 &&
    rotation !== 180 &&
    rotation !== 270
  ) {
    throw new RangeError('rotation must be 0, 90, 180, or 270')
  }
  assertIntegerInRange(
    straightening,
    -GIF_EDIT_MAX_STRAIGHTENING,
    GIF_EDIT_MAX_STRAIGHTENING,
    'straightening',
  )
  if (typeof flipHorizontal !== 'boolean') {
    throw new TypeError('flipHorizontal must be a boolean')
  }
  if (typeof flipVertical !== 'boolean') {
    throw new TypeError('flipVertical must be a boolean')
  }
  if (paletteMode !== 'perFrame' && paletteMode !== 'global') {
    throw new RangeError('paletteMode must be perFrame or global')
  }

  assertIntegerInRange(
    brightness,
    GIF_EDIT_MIN_ADJUSTMENT,
    GIF_EDIT_MAX_ADJUSTMENT,
    'brightness',
  )
  assertIntegerInRange(
    contrast,
    GIF_EDIT_MIN_ADJUSTMENT,
    GIF_EDIT_MAX_ADJUSTMENT,
    'contrast',
  )
  assertIntegerInRange(
    saturation,
    GIF_EDIT_MIN_ADJUSTMENT,
    GIF_EDIT_MAX_ADJUSTMENT,
    'saturation',
  )
  assertIntegerInRange(
    warmth,
    GIF_EDIT_MIN_ADJUSTMENT,
    GIF_EDIT_MAX_ADJUSTMENT,
    'warmth',
  )
  assertIntegerInRange(
    filterIntensity,
    GIF_EDIT_MIN_FILTER_INTENSITY,
    GIF_EDIT_MAX_FILTER_INTENSITY,
    'filterIntensity',
  )
  if (
    filter !== 'none' &&
    filter !== 'grayscale' &&
    filter !== 'sepia' &&
    filter !== 'vivid'
  ) {
    throw new RangeError('filter must be none, grayscale, sepia, or vivid')
  }
}

export const createIdentityGifEditRecipe = (
  overrides: Partial<GifEditRecipe> = {},
): GifEditRecipe => ({
  crop: IDENTITY_GIF_EDIT_CROP,
  rotation: 0,
  straightening: 0,
  flipHorizontal: false,
  flipVertical: false,
  brightness: 0,
  contrast: 0,
  saturation: 0,
  warmth: 0,
  filter: 'none',
  filterIntensity: 0,
  paletteMode: 'perFrame',
  ...overrides,
})

export const isIdentityGifEditGeometry = (recipe: GifEditRecipe): boolean =>
  recipe.crop.x === 0 &&
  recipe.crop.y === 0 &&
  recipe.crop.width === 1 &&
  recipe.crop.height === 1 &&
  recipe.rotation === 0 &&
  recipe.straightening === 0 &&
  !recipe.flipHorizontal &&
  !recipe.flipVertical

export const isIdentityGifEditRecipe = (recipe: GifEditRecipe): boolean =>
  isIdentityGifEditGeometry(recipe) && isIdentityGifEditAppearance(recipe)
